import axios from 'axios';
import { getKey, clearKey } from './auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  maxContentLength: 500 * 1024 * 1024, // 500MB
  maxBodyLength: 500 * 1024 * 1024, // 500MB
});

// Attach API key to every request
api.interceptors.request.use(config => {
  const key = getKey();
  if (key) config.headers['X-API-Token'] = key;
  return config;
});

// On 401, clear stored key and redirect to login
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401 && window.location.pathname !== '/login') {
      clearKey();
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// Test Cases
export const testCaseAPI = {
  getAll: (project) => {
    const params = project ? { project } : {};
    return api.get('/test-cases', { params });
  },
  getById: (id) => api.get(`/test-cases/${id}`),
  create: (data) => api.post('/test-cases', data),
  update: (id, data) => api.put(`/test-cases/${id}`, data),
  delete: (id) => api.delete(`/test-cases/${id}`),
  getProjects: () => api.get('/test-cases/meta/projects'),
};

// Test Results
export const testResultAPI = {
  getAll: (filters) => {
    return api.get('/test-results', { params: filters });
  },
  getById: (id) => api.get(`/test-results/${id}`),
  getS3Results: (id, params = {}) => api.get(`/test-results/${id}/s3-results`, { params }),
  getS3DownloadUrl: (id) => api.get(`/test-results/${id}/s3-download-url`),
  delete: (id) => api.delete(`/test-results/${id}`),
  getFilterMetadata: () => api.get('/test-results/meta/filters'),
  getEvolution: (testCaseId, filters) => {
    return api.get(`/test-results/evolution/${testCaseId}`, { params: filters });
  },
  compare: (resultIds) => api.post('/test-results/compare', { resultIds }),
  updateStage: (id, stage) => api.put(`/test-results/${id}/stage`, { stage }),
  updateTraffic: (id, manualTraffic) => api.put(`/test-results/${id}/traffic`, { manualTraffic }),
};

// CSV
export const csvAPI = {
  parse: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/csv/parse', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  process: (data, mapping, config) => {
    return api.post('/csv/process', { data, mapping, config });
  },
};

// Execute
export const executeAPI = {
  run: (testCaseId, config) => {
    return api.post(`/execute/${testCaseId}`, config, {
      timeout: 900000, // 15 minutes timeout for long-running tests
    });
  },
};

// Datasets
export const datasetAPI = {
  getAll: (tags) => {
    const params = tags ? { tags } : {};
    return api.get('/datasets', { params });
  },
  getById: (id) => api.get(`/datasets/${id}`),
  getPreview: (id) => api.get(`/datasets/${id}/preview`),
  download: (id) => api.get(`/datasets/${id}/download`, { responseType: 'blob' }),
  upload: (file, name, description, tags) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    if (description) formData.append('description', description);
    if (tags) formData.append('tags', tags);
    return api.post('/datasets/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  update: (id, data) => api.put(`/datasets/${id}`, data),
  delete: (id) => api.delete(`/datasets/${id}`),
  getTags: () => api.get('/datasets/meta/tags'),
};

// Proxies
export const proxyAPI = {
  getAll: (tags, isActive) => {
    const params = {};
    if (tags) params.tags = tags;
    if (isActive !== undefined) params.isActive = isActive;
    return api.get('/proxies', { params });
  },
  getById: (id) => api.get(`/proxies/${id}`),
  create: (data) => api.post('/proxies', data),
  update: (id, data) => api.put(`/proxies/${id}`, data),
  delete: (id) => api.delete(`/proxies/${id}`),
  getTags: () => api.get('/proxies/meta/tags'),
  getTraffic: (id, queryParams) => {
    return api.get(`/proxies/${id}/traffic`, { params: queryParams });
  },
};

// Proxy Lists
export const proxyListAPI = {
  getAll: (tags, isActive) => {
    const params = {};
    if (tags) params.tags = tags;
    if (isActive !== undefined) params.isActive = isActive;
    return api.get('/proxy-lists', { params });
  },
  getById: (id) => api.get(`/proxy-lists/${id}`),
  create: (data) => api.post('/proxy-lists', data),
  update: (id, data) => api.put(`/proxy-lists/${id}`, data),
  delete: (id) => api.delete(`/proxy-lists/${id}`),
  getTags: () => api.get('/proxy-lists/meta/tags'),
};

// Endpoint Performance
export const performanceAPI = {
  getAll: (project) => {
    const params = project ? { project } : {};
    return api.get('/performance', { params });
  },
  getById: (id) => api.get(`/performance/${id}`),
  getByTestCase: (testCaseId) => api.get(`/performance/test-case/${testCaseId}`),
  calculate: (testCaseId, data) => api.post(`/performance/calculate/${testCaseId}`, data),
  update: (id, data) => api.put(`/performance/${id}`, data),
  delete: (id) => api.delete(`/performance/${id}`),
  getProjects: () => api.get('/performance/meta/projects'),
};

// Production Tests
export const productionTestAPI = {
  getAll: () => api.get('/production-tests'),
  getById: (id) => api.get(`/production-tests/${id}`),
  create: (data) => api.post('/production-tests', data),
  update: (id, data) => api.put(`/production-tests/${id}`, data),
  delete: (id) => api.delete(`/production-tests/${id}`),
  run: (id) => api.post(`/production-tests/${id}/run`, {}, { timeout: 900000 }),
  getResults: (id) => api.get(`/production-tests/${id}/results`),
  deleteResult: (id, resultId) => api.delete(`/production-tests/${id}/results/${resultId}`),
  getFolders: () => api.get('/production-tests/meta/folders'),
  moveToFolder: (ids, folder) => api.put('/production-tests/bulk/folder', { ids, folder }),
  renameFolder: (oldName, newName) => api.put('/production-tests/bulk/rename-folder', { oldName, newName })
};

export default api;
